import React, { useState, useEffect } from "react"
import { Routes, Route, Link, useLocation, Navigate, useNavigate } from "react-router-dom"
import { Helmet } from "react-helmet-async"
import {Package, Store, Users, FileText, Image, ChevronLeft, ChevronRight, LayoutDashboard, ShoppingCart, Tag, UserCircle, LogOut, Shield, Globe, ShoppingBag, Coins, Receipt, Settings, Briefcase, RefreshCw, Building2} from 'lucide-react'
import { lumi } from "../lib/lumi"

// 導入管理元件
import ProductManager from "../components/ProductManager"
import ProductEditor from "../components/ProductEditor"
import StoreManager from "../components/StoreManager"
import ArticleManager from "../components/ArticleManager"
import ArticleEditor from "../components/ArticleEditor"
import PartnerManager from "../components/PartnerManager"
import PhotoManager from "../components/PhotoManager"
import PhotoEditPage from "./PhotoEditPage"
import AdminUserManager from "../components/AdminUserManager"
import CustomerManager from "../components/CustomerManager"
import CustomerTagManager from "../components/CustomerTagManager"
import CouponManager from "../components/CouponManager"
import OrderManager from "../components/OrderManager"
import PageSEOManager from "../components/PageSEOManager"
import ConvenienceStoreManager from "../components/ConvenienceStoreManager"
import ProductCategoryManager from "../components/ProductCategoryManager"
import PointsSettingsManager from "../components/PointsSettingsManager"
import PointsTransactionsManager from "../components/PointsTransactionsManager"
import SiteSettingsManager from "../components/SiteSettingsManager"
import B2BLeadsManager from "../components/B2BLeadsManager"
import B2BAccountsManager from "../components/B2BAccountsManager"
import SubscriptionManager from "../components/SubscriptionManager"

const AdminDashboard: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const [user, setUser] = useState(lumi.auth.user)
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null)
  const [redirectCountdown, setRedirectCountdown] = useState(3)
  const location = useLocation()
  const navigate = useNavigate()

  // 檢查用戶是否在 admin_users 中
  const checkAdminPermission = async (userEmail: string) => {
    try {
      const normalizedEmail = userEmail.trim().toLowerCase()
      console.log('[AdminDashboard] 🔍 Checking admin permission for:', normalizedEmail)
      
      const response = await lumi.entities.admin_users.list({})
      const adminUsers = response?.list || []
      
      console.log('[AdminDashboard] 📋 Admin users:', adminUsers)
      console.log('[AdminDashboard] 📋 Admin users count:', adminUsers?.length || 0)

      const matchedUser = adminUsers?.find((admin: any) => 
        admin.email?.trim().toLowerCase() === normalizedEmail
      )
      
      if (matchedUser) {
        console.log('[AdminDashboard] ✅ User is authorized:', matchedUser)
        setIsAuthorized(true)
      } else {
        console.log('[AdminDashboard] ❌ User NOT found')
        console.log('[AdminDashboard] Available emails:', adminUsers?.map((u: any) => u.email))
        setIsAuthorized(false)
      }
    } catch (error) {
      console.error('[AdminDashboard] ❌ Error:', error)
      setIsAuthorized(false)
    } finally {
      setIsLoading(false)
    }
  }

  // 監聽登入狀態變化
  useEffect(() => {
    const unsubscribe = lumi.auth.onAuthChange(({ user }) => {
      setUser(user)
      if (user?.email) {
        checkAdminPermission(user.email)
      } else {
        setIsLoading(false)
        setIsAuthorized(null)
      }
    })

    // 初始化檢查
    if (user?.email) {
      checkAdminPermission(user.email)
    } else {
      setIsLoading(false)
    }

    return unsubscribe
  }, [])

  // 倒數計時並跳轉首頁
  useEffect(() => {
    if (isAuthorized === false) {
      const timer = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            navigate('/zh')
            return 0
          }
          return prev - 1
        })
      }, 1000)

      return () => clearInterval(timer)
    }
  }, [isAuthorized, navigate])

  // 登入處理
  const handleSignIn = async () => {
    try {
      await lumi.auth.signIn()
    } catch (error) {
      console.error("登入失敗:", error)
      alert("登入失敗，請稍後再試")
    }
  }

  // 登出處理
  const handleSignOut = () => {
    if (confirm("確定要登出嗎？")) {
      lumi.auth.signOut()
    }
  }

  const menuItems = [
    {
      title: "總覽",
      icon: LayoutDashboard,
      href: "/admin",
      color: "bg-gray-700"
    },
    {
      title: "使用者管理",
      icon: Shield,
      href: "/admin/users",
      color: "bg-red-600"
    },
    {
      title: "會員管理",
      icon: UserCircle,
      href: "/admin/customers",
      color: "bg-indigo-600"
    },
    {
      title: "會員標籤",
      icon: Tag,
      href: "/admin/customer-tags",
      color: "bg-violet-600"
    },
    {
      title: "點數交易記錄",
      icon: Receipt,
      href: "/admin/points-transactions",
      color: "bg-lime-600"
    },
    {
      title: "訂單管理",
      icon: ShoppingCart,
      href: "/admin/orders",
      color: "bg-yellow-600"
    },
    {
      title: "優惠券管理",
      icon: Tag,
      href: "/admin/coupons",
      color: "bg-pink-600"
    },
    {
      title: "商品管理",
      icon: Package,
      href: "/admin/products",
      color: "bg-blue-600"
    },
    {
      title: "商品分類",
      icon: Tag,
      href: "/admin/product-categories",
      color: "bg-sky-600"
    },
    {
      title: "分店管理",
      icon: Store,
      href: "/admin/stores",
      color: "bg-green-600"
    },
    // ⚠️ 超商管理已停用（改用 ezShip 電子地圖）
    // {
    //   title: "超商管理",
    //   icon: ShoppingBag,
    //   href: "/admin/convenience-stores",
    //   color: "bg-emerald-600"
    // },
    {
      title: "訂閱出貨管理",
      icon: RefreshCw,
      href: "/admin/subscriptions",
      color: "bg-emerald-600"
    },
    {
      title: "B2B 詢問管理",
      icon: Briefcase,
      href: "/admin/b2b-leads",
      color: "bg-orange-500"
    },
    {
      title: "B2B 帳戶管理",
      icon: Building2,
      href: "/admin/b2b-accounts",
      color: "bg-blue-700"
    },
    {
      title: "合作夥伴",
      icon: Users,
      href: "/admin/partners",
      color: "bg-purple-600"
    },
    {
      title: "內容管理",
      icon: FileText,
      href: "/admin/lab",
      color: "bg-orange-600"
    },
    {
      title: "照片管理",
      icon: Image,
      href: "/admin/photos",
      color: "bg-teal-600"
    },
    {
      title: "分頁 SEO",
      icon: Globe,
      href: "/admin/seo",
      color: "bg-cyan-600"
    },
    {
      title: "網站設定",
      icon: Settings,
      href: "/admin/site-settings",
      color: "bg-slate-600"
    },
    {
      title: "动态sitemap",
      icon: Globe,
      href: "https://api.lumi.new/v1/functions/p373839333138763776/generateSitemap",
      color: "bg-emerald-600",
      external: true
    }
  ]

  const isActive = (href: string) => {
    if (href === "/admin") {
      return location.pathname === "/admin"
    }
    return location.pathname.startsWith(href)
  }

  // 載入中狀態
  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>ONGU COFFEE SYSTEM - Loading...</title>
        </Helmet>
        <div className="flex items-center justify-center min-h-screen bg-black">
          <div className="text-gray-400 text-lg">載入中...</div>
        </div>
      </>
    )
  }

  // 無權限：顯示錯誤訊息並倒數跳轉
  if (isAuthorized === false) {
    return (
      <>
        <Helmet>
          <title>ONGU COFFEE SYSTEM - Access Denied</title>
        </Helmet>
        <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="max-w-md w-full p-8 bg-gray-950 border border-red-800">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield size={40} className="text-white" />
            </div>
            <h1 className="text-2xl font-light tracking-wider text-gray-100 mb-2">
              權限不足
            </h1>
            <p className="text-gray-400">Access Denied</p>
          </div>

          <div className="mb-6 p-4 bg-red-950 border border-red-800">
            <p className="text-sm text-gray-200 mb-2">
              您的帳號尚未獲得管理後台權限
            </p>
            <p className="text-xs text-gray-400">
              如需存取權限，請聯絡系統管理員
            </p>
          </div>

          <div className="text-center p-4 bg-gray-900 border border-gray-800">
            <p className="text-gray-300 mb-2">
              {redirectCountdown} 秒後自動返回首頁...
            </p>
            <button
              onClick={() => navigate('/zh')}
              className="text-blue-400 hover:text-blue-300 text-sm underline"
            >
              立即返回
            </button>
          </div>
        </div>
      </div>
      </>
    )
  }

  // 未登入：顯示登入頁面
  if (!user) {
    return (
      <>
        <Helmet>
          <title>ONGU COFFEE SYSTEM - Login</title>
        </Helmet>
        <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="max-w-md w-full p-8 bg-gray-950 border border-gray-800">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-light tracking-wider text-gray-100 mb-2">
              ONGU COFFEE SYSTEM
            </h1>
            <p className="text-gray-400">管理後台登入</p>
          </div>

          <div className="mb-6 p-4 bg-gray-900 border border-gray-800">
            <p className="text-sm text-gray-300 mb-2">
              <Shield size={16} className="inline mr-2 text-yellow-500" />
              此為內部管理系統
            </p>
            <p className="text-xs text-gray-500">
              請使用您的 ONGU Coffee 員工帳號登入
            </p>
          </div>

          <button
            onClick={handleSignIn}
            className="w-full py-4 bg-blue-600 text-white hover:bg-blue-700 transition-colors text-lg tracking-wide"
          >
            使用 Lumi 平台登入
          </button>

          <div className="mt-6 text-center text-xs text-gray-500">
            <p>如無帳號請聯絡系統管理員</p>
          </div>
        </div>
      </div>
      </>
    )
  }

  // 已登入：顯示管理後台
  return (
    <>
      <Helmet>
        <title>ONGU COFFEE SYSTEM - Admin Dashboard</title>
      </Helmet>
      <div className="flex h-screen bg-black text-gray-100 overflow-hidden">
      {/* 左側導航欄 */}
      <aside
        className={`${
          isSidebarCollapsed ? "w-20" : "w-64"
        } bg-gray-950 border-r border-gray-800 transition-all duration-300 flex flex-col h-screen`}
      >
        {/* Logo 區域 */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-800">
          {!isSidebarCollapsed && (
            <div>
              <h1 className="text-lg font-light tracking-wider text-gray-100">
                ONGU COFFEE SYSTEM
              </h1>
              <p className="text-xs text-gray-500">管理後台</p>
            </div>
          )}
          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className="p-2 hover:bg-gray-800 rounded transition-colors ml-auto"
            aria-label={isSidebarCollapsed ? "展開側邊欄" : "收合側邊欄"}
          >
            {isSidebarCollapsed ? (
              <ChevronRight size={20} className="text-gray-400" />
            ) : (
              <ChevronLeft size={20} className="text-gray-400" />
            )}
          </button>
        </div>

        {/* 使用者資訊 */}
        <div className="px-4 py-4 border-b border-gray-800">
          {!isSidebarCollapsed ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-medium">
                {(user.userName || 'U').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-100 truncate">
                  {user.userName || 'User'}
                </p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
            </div>
          ) : (
            <div className="flex justify-center">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-medium">
                {(user.userName || 'U').charAt(0).toUpperCase()}
              </div>
            </div>
          )}
        </div>

        {/* 導航選單 */}
        <nav className="flex-1 py-6 overflow-y-auto">
          <ul className="space-y-2 px-3">
            {menuItems.map((item) => {
              const IconComponent = item.icon
              const active = isActive(item.href)

              return (
                <li key={item.href}>
                  {(item as any).external ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-3 py-3 rounded transition-all duration-200 text-gray-400 hover:bg-gray-900 hover:text-gray-200"
                      title={isSidebarCollapsed ? item.title : ""}
                    >
                      <div
                        className={`w-10 h-10 ${item.color} flex items-center justify-center flex-shrink-0`}
                      >
                        <IconComponent size={20} className="text-white" />
                      </div>
                      {!isSidebarCollapsed && (
                        <span className="font-light tracking-wide">
                          {item.title}
                        </span>
                      )}
                    </a>
                  ) : (
                    <Link
                      to={item.href}
                      className={`flex items-center gap-3 px-3 py-3 rounded transition-all duration-200 ${
                        active
                          ? "bg-gray-800 text-white border-l-4 border-gray-400"
                          : "text-gray-400 hover:bg-gray-900 hover:text-gray-200"
                      }`}
                      title={isSidebarCollapsed ? item.title : ""}
                    >
                      <div
                        className={`w-10 h-10 ${item.color} flex items-center justify-center flex-shrink-0`}
                      >
                        <IconComponent size={20} className="text-white" />
                      </div>
                      {!isSidebarCollapsed && (
                        <span className="font-light tracking-wide">
                          {item.title}
                        </span>
                      )}
                    </Link>
                  )}
                </li>
              )
            })}
          </ul>
        </nav>

        {/* 登出按鈕 */}
        <div className="p-4 border-t border-gray-800">
          <button
            onClick={handleSignOut}
            className={`flex items-center gap-3 px-3 py-3 w-full text-gray-400 hover:bg-gray-900 hover:text-red-400 transition-all duration-200 ${
              isSidebarCollapsed ? "justify-center" : ""
            }`}
            title={isSidebarCollapsed ? "登出" : ""}
          >
            <LogOut size={20} />
            {!isSidebarCollapsed && (
              <span className="font-light tracking-wide">登出</span>
            )}
          </button>
        </div>

        {/* 底部資訊 */}
        {!isSidebarCollapsed && (
          <div className="p-4 border-t border-gray-800 text-xs text-gray-500">
            <p>電商＋多分店＋B2B批發</p>
            <p>＋帳務一體化管理系統</p>
          </div>
        )}
      </aside>

      {/* 右側主要內容區 */}
      <main className="flex-1 overflow-y-auto bg-gray-900">
        <Routes>
          {/* 總覽頁面 */}
          <Route
            path="/"
            element={
              <div className="p-8 h-full bg-gray-900">
                {/* 頁面標題 */}
                <div className="mb-8">
                  <h2 className="text-3xl font-light tracking-wider mb-2">
                    系統總覽
                  </h2>
                  <p className="text-gray-400">快速查看系統運營數據</p>
                </div>

                {/* 統計卡片 */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  <div className="bg-gray-950 border border-gray-800 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-blue-600 flex items-center justify-center">
                        <Package size={24} className="text-white" />
                      </div>
                    </div>
                    <div className="text-3xl font-light text-gray-100 mb-2">
                      --
                    </div>
                    <div className="text-sm text-gray-400">總商品數</div>
                  </div>

                  <div className="bg-gray-950 border border-gray-800 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-green-600 flex items-center justify-center">
                        <Store size={24} className="text-white" />
                      </div>
                    </div>
                    <div className="text-3xl font-light text-gray-100 mb-2">
                      --
                    </div>
                    <div className="text-sm text-gray-400">活躍分店</div>
                  </div>

                  <div className="bg-gray-950 border border-gray-800 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-purple-600 flex items-center justify-center">
                        <Users size={24} className="text-white" />
                      </div>
                    </div>
                    <div className="text-3xl font-light text-gray-100 mb-2">
                      --
                    </div>
                    <div className="text-sm text-gray-400">B2B夥伴</div>
                  </div>

                  <div className="bg-gray-950 border border-gray-800 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 bg-orange-600 flex items-center justify-center">
                        <FileText size={24} className="text-white" />
                      </div>
                    </div>
                    <div className="text-3xl font-light text-gray-100 mb-2">
                      --
                    </div>
                    <div className="text-sm text-gray-400">發表文章</div>
                  </div>
                </div>

                {/* 快速操作 */}
                <div>
                  <h3 className="text-xl font-light tracking-wider mb-4">
                    快速操作
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {menuItems.slice(1).map((item) => {
                      const IconComponent = item.icon
                      return (
                        <Link
                          key={item.href}
                          to={item.href}
                          className="group block p-6 bg-gray-950 border border-gray-800 hover:border-gray-600 transition-all duration-200"
                        >
                          <div className="flex items-center mb-3">
                            <div
                              className={`w-10 h-10 ${item.color} flex items-center justify-center mr-3`}
                            >
                              <IconComponent size={20} className="text-white" />
                            </div>
                            <h4 className="text-lg font-light tracking-wide text-gray-100 group-hover:text-white">
                              {item.title}
                            </h4>
                          </div>
                        </Link>
                      )
                    })}
                  </div>
                </div>
              </div>
            }
          />

          {/* 功能頁面路由 */}
          <Route path="users" element={<AdminUserManager />} />
          <Route path="customers" element={<CustomerManager />} />
          <Route path="customer-tags" element={<CustomerTagManager />} />
          <Route 
            path="orders" 
            element={
              <OrderManager 
                initialSearchEmail={
                  new URLSearchParams(window.location.search).get("email") || ""
                } 
              />
            } 
          />
          <Route path="coupons" element={<CouponManager />} />
          <Route path="products" element={<ProductManager />} />
          <Route path="product-categories" element={<ProductCategoryManager />} />
          <Route path="products/new" element={<ProductEditor />} />
          <Route path="products/edit/:id" element={<ProductEditor />} />
          <Route path="stores" element={<StoreManager />} />
          <Route path="convenience-stores" element={<ConvenienceStoreManager />} />
          <Route path="partners" element={<PartnerManager />} />
          <Route path="lab" element={<ArticleManager />} />
          <Route path="lab/new" element={<ArticleEditor />} />
          <Route path="lab/edit/:id" element={<ArticleEditor />} />
          <Route path="photos" element={<PhotoManager />} />
          <Route path="photos/edit/:id" element={<PhotoEditPage />} />
          <Route path="seo" element={<PageSEOManager />} />
          <Route path="points-settings" element={<PointsSettingsManager />} />
          <Route path="points-transactions" element={<PointsTransactionsManager />} />
          <Route path="site-settings" element={<SiteSettingsManager />} />
          <Route path="b2b-leads" element={<B2BLeadsManager />} />
          <Route path="b2b-accounts" element={<B2BAccountsManager />} />
          <Route path="subscriptions" element={<SubscriptionManager />} />

          {/* 404 重定向 */}
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
    </>
  )
}

export default AdminDashboard
